<script>
    import { assets, searchAssets } from "./assets.state.svelte.js";
    import AdvancedSearch from "../../components/powerTable/advanced-search/index.svelte";
    import tagService from "../tag/service.js";
    import BreadCrumNav from "../../components/breadcrum-nav.svelte";
    import List from "./_components/list.svelte";
    import { goto, params } from "@roxi/routify";
    import { format } from "svelte-i18n";
    import Spinner from "../../components/spinner.svelte";
    import { untrack } from "svelte";
    import dayjs from "dayjs";

    // Safely bind router navigation synchronously at root level during component setup
    const redirect = $goto;

    let isInitialLoading = $state(true);
    let tags = $state([]);
    let associations = $state([]);
    let selected_assets = $state({
        data: [],
    });

    // Cleanly derive active search filters directly from reactive URL parameters
    let advancedSearchFilter = $derived($params || {});

    const getTags = async (config = { filter: {} }) => {
        tags = await tagService.search("v2/tag/", config);
        return tags;
    };

    const getTagAssociations = async () => {
        associations = await tagService.search("v2/tag/association/", {
            target_object_type: "Asset",
        });
        return associations;
    };

    // Unified data loader triggered cleanly by URL parameter changes
    const loadAssetData = async (urlParams = {}) => {
        const [tagsRes, assocRes] = await Promise.all([
            getTags(),
            getTagAssociations(),
        ]);

        const filterConfig = { ...urlParams, include_finding_count: true };

        await searchAssets("v2/asset/", {
            filter: filterConfig,
            buildList: {
                tags: { elements: tagsRes, associations: assocRes },
            },
        });

        if (isInitialLoading) isInitialLoading = false;
    };

    // Automatically fetch data on initial load and whenever URL parameters change (Search, Back/Forward arrows)
    $effect(() => {
        const currentUrlParams = JSON.parse(JSON.stringify($params || {}));
        untrack(() => {
            loadAssetData(currentUrlParams);
        });
    });

    const handleAdvancedSearch = (event) => {
        let filters = { ...(event.detail || {}) };

        // Normalize date ranges
        if (filters.first_discovery_at && filters.first_discovery_at.includes(" - ")) {
            let dates = filters.first_discovery_at.split(" - ");
            filters.first_discovery_at__gte = dayjs(dates[0].replaceAll("/", "-")).format("YYYY-MM-DD") + "T00:00:00.000-03:00";
            filters.first_discovery_at__lte = dayjs(dates[1].replaceAll("/", "-")).format("YYYY-MM-DD") + "T23:59:59.999-03:00";
            delete filters.first_discovery_at;
        }

        if (filters.last_discovery_at && filters.last_discovery_at.includes(" - ")) {
            let dates = filters.last_discovery_at.split(" - ");
            filters.last_discovery_at__gte = dayjs(dates[0].replaceAll("/", "-")).format("YYYY-MM-DD") + "T00:00:00.000-03:00";
            filters.last_discovery_at__lte = dayjs(dates[1].replaceAll("/", "-")).format("YYYY-MM-DD") + "T23:59:59.999-03:00";
            delete filters.last_discovery_at;
        }

        if (filters.in_scope && filters.in_scope === "all") {
            delete filters.in_scope;
        }

        let reqConfig = {};
        for (let [k, v] of Object.entries(filters)) {
            if (v !== "" && v !== null && v !== undefined) {
                if (typeof v === "object" && Array.isArray(v)) {
                    if (v.length > 0 && v[0] !== "") {
                        reqConfig[k] = v.join(",");
                    }
                } else {
                    reqConfig[k] = v;
                }
            }
        }

        // Use Routify v3 navigation to update the URL; the $effect will detect the parameter change and load data
        redirect(".", reqConfig);
    };
</script>

{#if isInitialLoading}
    <div
        class="d-flex flex-column justify-content-center"
        style="height: 80vh;"
    >
        <Spinner />
    </div>
{:else}
    <section class="content-header">
        <div class="container-fluid">
            <div class="row">
                <div class="col-6">
                    <h1 class="mt-3 mb-3 page-title">
                        <i class="fa-xs fas fa-archive"></i>
                        {$format(`asset.plural`)}
                    </h1>
                </div>
                <div class="col-6">
                    <BreadCrumNav
                        routes={[{ label: $format(`asset.plural`) }]}
                    />
                </div>
            </div>
        </div>
    </section>
    <section class="content">
        <div class="container-fluid">
            <div class="card card-secondary card-outline">
                <div class="card-header">
                    <h4 class="card-title no-export">
                        {$format("components.advancedSearch.search")}
                    </h4>
                </div>
                <div class="card-body pe-5">
                    {#key JSON.stringify(advancedSearchFilter)}
                        {#if assets.loading}
                            <div class="overlay-wrapper">
                                <div
                                    class="text-center"
                                    class:overlay={assets.loading}
                                >
                                    <i class="fas fa-2x fa-sync-alt fa-spin"></i>
                                </div>
                            </div>
                        {/if}

                        <AdvancedSearch
                            config={{
                                inputs: [
                                    [
                                        {
                                            type: "multiple",
                                            name: "kind__in",
                                            id: "assetKindMultiple",
                                            label: $format(
                                                "components.advancedSearch.asset_kind",
                                            ),
                                            options: [
                                                {
                                                    id: "domain",
                                                    name: $format(
                                                        "asset.kind.domain.singular",
                                                    ),
                                                },
                                                {
                                                    id: "hostname",
                                                    name: "Hostname",
                                                },
                                                {
                                                    id: "public_network",
                                                    name: $format(
                                                        "asset.kind.public_network.singular",
                                                    ),
                                                },
                                                {
                                                    id: "private_network",
                                                    name: $format(
                                                        "asset.kind.private_network.singular",
                                                    ),
                                                },
                                                {
                                                    id: "public_ip",
                                                    name: $format(
                                                        "asset.kind.public_ip.singular",
                                                    ),
                                                },
                                                {
                                                    id: "private_ip",
                                                    name: $format(
                                                        "asset.kind.private_ip.singular",
                                                    ),
                                                },
                                                {
                                                    id: "ad",
                                                    name: $format(
                                                        "asset.kind.ad.singular",
                                                    ),
                                                },
                                                {
                                                    id: "cloud",
                                                    name: $format(
                                                        "asset.kind.cloud.singular",
                                                    ),
                                                },
                                                {
                                                    id: "software",
                                                    name: $format(
                                                        "asset.kind.software.singular",
                                                    ),
                                                },
                                                {
                                                    id: "endpoint",
                                                    name: $format(
                                                        "asset.kind.endpoint.singular",
                                                    ),
                                                },
                                                { id: "email", name: "Email" },
                                                {
                                                    id: "container_image",
                                                    name: $format(
                                                        "asset.kind.container_image.singular",
                                                    ),
                                                },
                                            ],
                                        },
                                        {
                                            type: "select",
                                            name: "tag_id",
                                            id: "assetTagMultiple",
                                            label: $format(
                                                "components.advancedSearch.asset_tag",
                                            ),
                                            options:
                                                tagService.filterTagsByTargetObjectType(
                                                    tags,
                                                    associations,
                                                    "Asset",
                                                ),
                                        },
                                    ],
                                    [
                                        {
                                            type: "select",
                                            name: "in_scope",
                                            id: "assetInScope",
                                            label: $format("En alcance"),
                                            options: [
                                                { name: "Sí", id: true },
                                                { name: "No", id: false },
                                            ],
                                        },
                                        {
                                            type: "select",
                                            name: "finding_level__gte",
                                            id: "assetFindingLevel",
                                            label: $format(
                                                "components.advancedSearch.finding_level",
                                            ),
                                            options: [
                                                {
                                                    name: "Crítica",
                                                    id: "critical",
                                                },
                                                {
                                                    name: "Alta o superior",
                                                    id: "high",
                                                },
                                                {
                                                    name: "Media o superior",
                                                    id: "medium",
                                                },
                                                {
                                                    name: "Baja o superior",
                                                    id: "low",
                                                },
                                            ],
                                        },
                                    ],
                                    [
                                        {
                                            type: "date",
                                            name: "first_discovery_at",
                                            id: "assetDateRange",
                                            label: $format(
                                                "components.advancedSearch.found",
                                            ),
                                        },
                                        {
                                            type: "date",
                                            name: "last_discovery_at",
                                            id: "assetDateRangeSeen",
                                            label: $format(
                                                "components.advancedSearch.seen",
                                            ),
                                        },
                                    ],
                                ],
                                activeFilters: JSON.parse(
                                    JSON.stringify(advancedSearchFilter),
                                ),
                            }}
                            on:advancedSearch={handleAdvancedSearch}
                        />
                    {/key}
                </div>
            </div>
        </div>
    </section>
    <section class="content">
        <div class="container-fluid">
            <div class="row">
                <div class="col-12">
                    {#if assets.loading}
                        <div class="overlay-wrapper">
                            <div
                                class="text-center"
                                class:overlay={assets.loading}
                            >
                                <i class="fas fa-2x fa-sync-alt fa-spin"></i>
                            </div>
                        </div>
                    {/if}
                    <List {tags} kind={"allWithActions"} showActions={true} />
                </div>
            </div>
        </div>
    </section>
{/if}
<style>
</style>